<template>
  <div class="event-record">
    <NavHeaderBar :showSearch="false" :navBarTitle="$t('shared.title.eventRecord')" @backReturn="backReturn">
    </NavHeaderBar>

    <!-- Filter Section (Sticky) -->
    <van-sticky :offset-top="46">
      <EventFilter
        :product-options="globalData.brandProductList"
        :objKey="{label: 'productName', value: 'id'}"
        selection-mode="multiple"
        @filter-change="onFilterChange"
      />
    </van-sticky>

    <!-- Event List -->
    <div class="list-container">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list v-model="loading" :finished="finished" :finished-text="$t('tips.noMoreData')" @load="onLoad">
          <div v-for="(item, index) in eventList" :key="index" class="event-card" @click="toUrl('/eventDetail', item)">
            <!-- Header: Title & Status Tag -->
            <div class="card-header">
              <div class="title-row">
                <span class="event-title txt-ellipsis">{{ item.policy.policyName }}</span>
                <span class="status-tag txt-ellipsis">{{ item.policy.modality === 0 ? $t('shared.onSite') : item.policy.modality === 1 ? $t('shared.offSite') : item.policy.modality === 2 ? $t('shared.webSite') : '' }}</span>
              </div>
            </div>

            <!-- User Info -->
            <div class="info-row" v-if="item.createUserName">
              <img src="@/assets/images/map_user.png" class="row-icon" />
              <span class="info-text">{{ $t('shared.bA') }}: {{ item.createUserName }}</span>
            </div>

            <!-- Time Info & Status -->
            <div class="info-row">
              <img src="@/assets/images/map_time.png" class="row-icon" />
              <div class="time-content">
                <span class="time-status-badge" :class="{'status-not-started': item.status === 0, 'status-starting': item.status === 1, 'status-end': item.status === 2, 'status-archive': item.status === 10}">{{ item.status === 0 ? $t('shared.notStarted') : item.status === 1 ? $t('shared.startingNow') : item.status === 2 ? $t('shared.end') : item.status === 10 ? $t('shared.nonEditable') : '' }}</span>
                <span class="time-range">{{ item.startTime }} ~ {{ item.endTime }}</span>
              </div>
            </div>

            <!-- Tags -->
            <div class="info-row tags-row">
              <img src="@/assets/images/map_jian.png" class="row-icon" /> <!-- Placeholder icon if not exact -->
              <div class="tags-list">
                <span v-for="(tag, tIndex) in item.policy.policyProductList" :key="tIndex" class="product-tag" :style="{border: `1px solid ${tag.productColor}`, color: tag.productColor}">{{ tag.productName }}</span>
              </div>
            </div>

            <!-- Store Info -->
            <div class="info-row store-row">
              <img src="@/assets/images/store.png" class="row-icon" />
              <span class="store-name van-ellipsis">{{ item.customerName }}</span>
              <img src="@/assets/images/daohan1.png" class="nav-icon" />
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import { arsEventListPageApi } from '@/api/event'
import { Toast } from 'vant'
import { mapState, mapMutations } from 'vuex'
export default {
  name: 'EventIndex',
  components: {
    EventFilter: () => import('./components/EventFilter')
  },
  data () {
    return {
      // List State
      eventList: [],
      loading: false,
      finished: false,
      refreshing: false,
      page: 1,
      limit: 10,

      // Filter State
      productIdList: [], // Changed to array for multiple selection support
      startTime: '',
      endTime: '',
      customerId: ''
    }
  },
  computed: {
    ...mapState({
      globalData: 'globalData',
      userInfo: 'myUserInfo'
    }),
    ...mapState('event', ['eventInfo'])
  },
  activated () {
    this.customerId = this.$route.query.customerId
  },
  methods: {
    ...mapMutations('event', ['SET_EVENTINFO']),
    onFilterChange (data) {
      this.productIdList = data.product
      this.startTime = data.startDate
      this.endTime = data.endDate
      this.onRefresh()
    },
    // --- API & List Logic ---
    async onLoad () {
      if (this.refreshing) {
        this.eventList = []
        this.refreshing = false
      }

      if (this.page === 1) {
        this.eventList = []
      }

      try {
        const params = {
          page: this.page,
          limit: this.limit,
          // Add filter params here, map to actual API fields
          productType: this.productIdList,
          startTime: this.startTime,
          endTime: this.endTime,
          userIdList: [this.userInfo.user.id],
          isFillIn: 1,
          customerId: this.customerId
        }
        Toast.loading({
          message: this.$t('shared.loading'),
          forbidClick: true,
          duration: 0
        })
        const res = await arsEventListPageApi(params)
        Toast.clear()
        if (this.refreshing) {
          this.eventList = res.data.list
          this.refreshing = false
        } else {
          this.eventList = [...this.eventList, ...res.data.list]
        }

        this.page++

        if (this.eventList.length === Number(res.data.totalRow)) {
          this.finished = true
        }
      } catch (error) {
        console.error(error)
        this.finished = true
      } finally {
        this.loading = false
      }
    },
    onRefresh () {
      this.finished = false
      this.loading = true
      this.page = 1
      this.onLoad()
    },
    backReturn () {
      this.$destroy()
    },
    toUrl (url, params) {
      if (url === '/eventDetail') {
        this.$router.push(`${url}?eventId=${params.id}&pageType=`)
        this.SET_EVENTINFO({ ...this.eventInfo, eventId: params.id })
      } else {
        this.$router.push(url)
        this.$destroy()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.event-record {
  background-color: #f7f8fa;
  min-height: 100vh;
  padding-bottom: 80px; // Space for bottom button

  .right-header {
    display: flex;
    align-items: center;
  }

  .event-card {
    background: #fff;
    padding: 10px;
    border-bottom: 1px solid rgb(238, 229, 229);
    &:first-child {
      border-top: 1px solid rgb(238, 229, 229);
    }
    .card-header {
      margin-bottom: 8px;

      .title-row {
        display: flex;
        align-items: center;

        .event-title {
          max-width: 280px;
          font-size: 14px;
          font-weight: bold;
          color: #333;
          margin-right: 8px;
        }

        .status-tag {
          max-width: 66px;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 2px;
          color: #fff;
          background-color: #f59a23;
        }
      }
    }

    .info-row {
      display: flex;
      align-items: center;
      margin-bottom: 6px;
      font-size: 13px;
      color: #666;
      line-height: 1.4;

      .row-icon {
        width: 13px;
        height: 13px;
        margin-right: 6px;
        object-fit: contain;
      }

      .info-text {
        flex: 1;
        font-size: 12px;
      }

      .time-content {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .time-status-badge {
          font-size: 10px;
          padding: 1px 4px;
          border-radius: 2px;
          margin-right: 6px;
          border: 1px solid currentColor;
          &.status-not-started {
            color: #d45648;
            background: #f7c9ce;
            border-color: #d45648;
          }
          &.status-starting {
            color: #1f9d16;
            background: #d2f6d1;
            border-color: #1f9d16;
          }
          &.status-end {
            color: #898989;
            background: #e6e6e6;
            border-color: #898989;
          }
          &.status-archive {
            color: #307bff;
            border-color: #307bff;
            background: #e9effc;
          }
        }

        .time-range {
          color: #666;
          font-size: 12px;
        }
      }
    }

    .tags-row {
      align-items: flex-start;
      .tags-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;

        .product-tag {
          font-size: 10px;
          padding: 1px 6px;
          border-radius: 2px;
          border: 1px solid;
        }
      }
    }

    .store-row {
      margin-bottom: 0;
      align-items: center;

      .store-name {
        flex: 1;
        font-size: 12px;
        color: #666;
      }

      .nav-icon {
        width: 16px;
        height: 16px;
        margin-left: 8px;
        color: #1989fa;
      }
    }
  }
}
</style>
